import React, { Component } from "react";
import Flag from "react-world-flags";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { UserBank, UserBankStatus, AccountType } from "../../../../generated";
import {
  Clear,
  Delete,
  Done,
  Edit,
  FileCopy,
  QuestionAnswer,
  WatchLater,
} from "@mui/icons-material";
import { CopyToClipboard } from "react-copy-to-clipboard";

export class SelectedBanksList extends Component<
  {
    onDelete?: (bank: UserBank) => void;
    onEdit?: (bank: UserBank) => void;
    allowEdit?: boolean;
    allowDelete?: boolean;
    banks: UserBank[];
  },
  {}
> {
  shortenAccountNo(text: string) {
    if (!text || text.length <= 6) {
      return text;
    }
    return text.substring(0, 4) + "..." + text.substring(text.length - 2);
  }

  renderEmptyRow() {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          Nothing here
        </TableCell>
      </TableRow>
    );
  }

  renderRows(banks: UserBank[]) {
    const sortedBanks = banks.sort((a, b) => {
      return (
        a.displayName?.localeCompare(b.displayName) ||
        a.legalName?.localeCompare(b.legalName) ||
        0
      );
    });

    return sortedBanks.map((bank) => (
      <TableRow key={bank.rowKey}>
        <TableCell>
          <Flag
            code={bank.countryCode}
            alt={bank.countryCode}
            width="35px"
            height="25px"
            style={{ margin: "8px" }}
          />
        </TableCell>
        <TableCell>{bank.countryCode}</TableCell>
        <TableCell>{bank.displayName ?? bank.legalName}</TableCell>
        <TableCell>
          <Tooltip title={bank.accountType || ""}>
            <>
              {bank.accountType === undefined && ""}
              {bank.accountType === AccountType.PERSONAL && "Personal"}
              {bank.accountType === AccountType.BUSINESS && "Business"}
            </>
          </Tooltip>
        </TableCell>
        <TableCell>
          {this.shortenAccountNo(bank.iban)}
          {bank.iban && (
            <CopyToClipboard text={bank.iban}>
              <IconButton>
                <FileCopy />
              </IconButton>
            </CopyToClipboard>
          )}
        </TableCell>
        <TableCell>
          {this.shortenAccountNo(bank.bban)}
          {bank.bban && (
            <CopyToClipboard text={bank.bban}>
              <IconButton>
                <FileCopy />
              </IconButton>
            </CopyToClipboard>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={bank.status || ""}>
            <>
              {bank.status === UserBankStatus.AVAILABLE && (
                <Done className={"text-success"} />
              )}
              {bank.status === UserBankStatus.IN_PROGRESS && (
                <WatchLater className={"text-info"} />
              )}
              {bank.status === UserBankStatus.REQUESTED && (
                <QuestionAnswer className={"text-secondary"} />
              )}
              {bank.status === UserBankStatus.NOT_POSSIBLE && (
                <Clear className={"text-danger"} />
              )}
            </>
          </Tooltip>
        </TableCell>
        <TableCell>
          {this.props.allowEdit !== false && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => this.props.onEdit(bank)}
                color="primary"
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {this.props.allowDelete !== false && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => this.props.onDelete(bank)}
                color="primary"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    ));
  }

  renderTableBody(banks: UserBank[]) {
    return (
      <>{banks.length > 0 ? this.renderRows(banks) : this.renderEmptyRow()}</>
    );
  }

  render() {
    return (
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell width={50}>Country</TableCell>
              <TableCell width={120}>Bank name</TableCell>
              <TableCell>Account type</TableCell>
              <TableCell>IBAN</TableCell>
              <TableCell>BBAN</TableCell>
              <TableCell width={100}>Status</TableCell>
              <TableCell width={140} />
            </TableRow>
          </TableHead>
          <TableBody>{this.renderTableBody(this.props.banks)}</TableBody>
        </Table>
      </TableContainer>
    );
  }
}
